import axios from "axios";
import { getSessionToken } from "../modules/session/functions";
import { defineMessages } from "react-intl";

const API_URL = process.env.REACT_APP_API;
export const HTTP_UNAUTHORIZED = 401;
export const HTTP_INTERNAL_SERVER_ERROR = 500;

export const apiClientHooks = {
  onUnauthorized: [],
};

const translate = defineMessages({
  networkError: {
    id: "app.error.network_error",
    defaultMessage: "Es sieht so aus, als wären Sie offline...",
  },
  timeoutError: {
    id: "app.error.timeout",
    defaultMessage:
      "Die Anfrage hat zu lange gedauert. Bitte erneut versuchen.",
  },
});

// handle 401 requests
const addUnauthorizedInterceptor = (client) =>
  client.interceptors.response.use(null, (err) => {
    // worst case / show message and cancel request
    if (err.message === "Network Error") {
      // lazy require to avoid circular imports
      // eslint-disable-next-line global-require
      const store = require("../store").default;
      // eslint-disable-next-line global-require
      const { showNotificationMessage } = require("../modules/uiState");

      store.dispatch(
        showNotificationMessage(
          // missing formatMessage
          translate.networkError.defaultMessage,
        ),
      );
      return Promise.reject(err);
    }

    // timeout handling
    if (
      err.code === "ECONNABORTED" ||
      (err.message && err.message.toLowerCase().includes("timeout"))
    ) {
      // lazy require to avoid circular imports
      // eslint-disable-next-line global-require
      const store = require("../store").default;
      // eslint-disable-next-line global-require
      const { showNotificationMessage } = require("../modules/uiState");

      store.dispatch(
        showNotificationMessage(translate.timeoutError.defaultMessage),
      );
      return Promise.reject(err);
    }

    if (err.response && err.response.status === HTTP_UNAUTHORIZED) {
      apiClientHooks.onUnauthorized.forEach((fn) => fn());
    }

    return Promise.reject(err);
  });

// Pure params serializer: JSON-stringify `filter` when it's an object,
// and encode other params using encodeURIComponent. This makes it safe
// for callers to pass `params: { filter: { estateId: 890 } }` and have
// the query built as filter=%7B%22estateId%22%3A890%7D (URL-encoded JSON).
const paramsSerializer = (params = {}) => {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === null || typeof value === "undefined") return;

    if (key === "filter") {
      // If filter is an object, stringify it first. If it's already a
      // string, leave it as is.
      const filterVal =
        typeof value === "string" ? value : JSON.stringify(value);
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(filterVal)}`);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((v) => {
        parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`);
      });
      return;
    }

    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  });
  return parts.join("&");
};

export const apiClient = (options, bearerToken) => {
  const token = bearerToken || getSessionToken();

  const apiClient = axios.create({
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: `Bearer ${token}`,
    },
    // `baseURL` will be prepended to `url` unless `url` is absolute.
    baseURL: API_URL,
    // default request timeout (can be overridden per call via options)
    timeout: 10000,
    // custom params serializer to handle `filter` objects
    paramsSerializer,
    // upload, timeout, download, ..usw
    ...options,
  });

  addUnauthorizedInterceptor(apiClient);

  return apiClient;
};
